import React, { Suspense } from 'react';
import styled from '@emotion/styled';
import LoaderAnimationData from 'assets/images/animations/env0-loader.json';

const LottieComponent = React.lazy(() => import('lottie-react').then(module => ({ default: module.Lottie })));

export const loaderSizes = ['default', 'small', 'large'] as const;
export type LoaderSize = (typeof loaderSizes)[number];

export interface LoaderProps {
  size?: LoaderSize;
  className?: string;
  'data-e2e'?: string;
}

const SIZE_MAP: { [key in LoaderSize]: string } = {
  large: '150px',
  default: '100px',
  small: '50px'
};

export const Loader: React.FunctionComponent<LoaderProps> = ({
  size = 'default' as const,
  className,
  'data-e2e': dataE2e = 'loader'
}) => {
  const animation = { data: LoaderAnimationData, id: 'default' };

  return (
    <Container className={className} data-e2e={dataE2e}>
      <AnimationContainer size={size} data-e2e={`animation-container-${size}`}>
        <Suspense fallback={<CssSpinner data-e2e="loader-fallback-spinner" />}>
          <LottieComponent src={animation.data} data-e2e={`animation-${animation.id}`} loop autoplay />
        </Suspense>
      </AnimationContainer>
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AnimationContainer = styled.div<{ size: LoaderSize }>`
  height: ${({ size }) => SIZE_MAP[size]};
  width: ${({ size }) => SIZE_MAP[size]};
`;

// The Lottie animation is unchanged by the restyle (APO-466) — only this fallback, shown until
// lottie-react resolves, moves onto tokens.
const CssSpinner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border-radius: 50%;
    animation: loader-spin 0.8s linear infinite;

    // icon-action is what the generated bridge gives antd Spin, so the family's two spinners agree.
    // It measures 1.67:1 on the light page, below the 3:1 minimum - raised as a design-system
    // question rather than diverged here, since the token reaches every Spin in the app.
    border: 3px solid var(--icon-secondary);
    border-top-color: var(--icon-action);
  }

  @keyframes loader-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default Loader;
